import { uiState } from '../store/initialState'
import { isEqual, uniq } from 'lodash'
import { createSelector, createSelectorCreator, defaultMemoize } from 'reselect'
import { serializeNoFormatting } from '../slate_serializers/to_plain_text'

// Other selector dependencies
import { allBookIdsSelector, allBooksAsArraySelector, allBooksSelector } from './booksFirstOrder'
import {
  ideasFromPocketThatWereUsedSelector,
  selectedCharacterAttributeTabSelector,
  showBookTabsSelector,
} from './secondOrder'
import {
  characterTabSelector,
  outlineFulltextHoverDetailsSelector,
  selectedCharacterSelector,
  showOutlineFulltextCardTitleSelector,
  uiSelector,
  allIdeasSelector,
  ideasSearchTermSelector,
  allPreviewPanelsAsFlatArraySelector,
  allBookmarkedItemsSelector,
  isIdeaBoardOpenSelector,
  bookmarksSearchTermSelector,
  bookmarksByKindSelector,
  shouldShowPanelsWithIdeaBoardSelector,
} from './secondOrder'
import { ideasFromPocketSelector, threadsFromPocketSelector } from './ideasFirstOrder'
import { seriesSelector } from './seriesFirstOrder'
import { allLinesSelector } from './linesFirstOrder'
import {
  characterCustomAttributesSelector,
  cardsCustomAttributesSelector,
  noteCustomAttributesSelector,
  placeCustomAttributesSelector,
} from './customAttributesFirstOrder'
import { allBeatsSelector } from './beatsFirstOrder'
import { createAggressiveDeepEqualSelector } from './createDeepEqualSelector'
import { escapeUIPathElement } from '../helpers/ui'
import { allCardsSelector } from './cardsFirstOrder'
import { allNotesSelector } from './notesFirstOrder'
import { allCharactersSelector, charactersSortedAtoZSelector } from './charactersFirstOrder'
import { attributesSelector } from './attributesFirstOrder'
import { allPlacesSelector, placesSortedAtoZSelector } from './placesFirstOrder'
import { allTagsSelector, sortedTagsSelector } from './tagsFirstOrder'
import { characterSearchHits, literalRegExp, hits } from './entitySearch'
import { outOfOrderSearch } from '../helpers/outOfOrderSearch'

/**
 * @typedef {import('../index').PlottrState} PlottrState
 * @typedef {import('../index').Tag} Tag
 * @typedef {import('../index').Place} Place
 * @typedef {import('../index').Character} Character
 * @typedef {import('../index').IdeaItem} IdeaItem
 * @typedef {import('../index').Selection} Selection
 * @typedef {import('../index').DOMSelection} DOMSelection
 * @typedef {import('../types/plottr').Focus} Focus
 * @typedef {import('../types/plottr').DocumentationType} DocumentationType
 * @typedef {import('../types/plottr').DocumentationUi} DocumentationUi
 * @typedef {import('../types/plottr').PreviewPanelEntityKind} PreviewPanelEntityKind
 * @typedef {import('../types/plottr').BookmarkEntityKind} BookmarkEntityKind
 * @typedef {import('../types/plottr').SearchHit} SearchHit
 * @typedef {import('../types/plottr').BookmarkItem} BookmarkItem
 * @typedef {import('../types/plottr').CardDialogUi} CardDialogUi
 * @typedef {import('../types/plottr').ImportModalUiData} ImportModalUiData
 * @typedef {import('../types/plottr').BookDialog} BookDialog
 * @typedef {import('../types/plottr').ResturctureTimelineModalUi} ResturctureTimelineModalUi
 * @typedef {import('../types/plottr').CharacterCustomTemplate} CharacterCustomTemplate
 * @typedef {import('../types/plottr').CharacterTabCharacterEditor} CharacterTabCharacterEditor
 * @typedef {import('../types/plottr').SearchDialogUi} SearchDialogUi
 * @typedef {import('../types/plottr').OutlineTabUi} OutlineTabUi
 * @typedef {import('../types/plottr').OutlineCardEditor} OutlineCardEditor
 * @typedef {import('../types/plottr').NoteTabUi} NoteTabUi
 * @typedef {import('../types/plottr').PlaceTabUi} PlaceTabUi
 * @typedef {import('../types/plottr').TagTabUi} TagTabUi
 * @typedef {import('../types/plottr').ProjectTabUi} ProjectTabUi
 * @typedef {import('../types/plottr').TimelineUi} TimelineUi
 * @typedef {import('../types/plottr').DashboardModalUi} DashboardModalUi
 * @typedef {import('../types/plottr').DashboardModalView} DashboardModalView
 * @typedef {import('../types/plottr').ImportModalUi} ImportModalUi
 * @typedef {import('../types/plottr').ImportModalUiBookData} ImportModalUiBookData
 * @typedef {import('../types/plottr').ConflictResolutionModalUi} ConflictResolutionModalUi
 * @typedef {import('../types/plottr').EntityKind} EntityKind
 * @typedef {import('../types/plottr').MobileControlsModal} MobileControlsModal
 * @typedef {import('../types/system').CharacterTemplate} CharacterTemplate
 * @typedef {import('../types/system').Idea} Idea
 * @typedef {import('../types/system').Ideas} Ideas
 * @typedef {import('../types/system').Threads} Threads
 * @typedef {import('../types/plottr-in-your-pocket').Idea} PocketIdea
 */

/**
 * @type {function(PlottrState): CardDialogUi}
 */
export const cardDialogSelector = createSelector(uiSelector, ({ cardDialog }) => {
  return cardDialog ?? {}
})
/**
 * @type {function(PlottrState): BookDialog}
 */
export const bookDialogSelector = createSelector(uiSelector, ({ bookDialog }) => {
  return bookDialog
})

/**
 * @type {function(PlottrState): (Null | number)}
 */
export const cardDialogCardIdSelector = createSelector(cardDialogSelector, ({ cardId }) => {
  return cardId
})
/**
 * @type {function(PlottrState): (Null | number)}
 */
export const cardDialogLineIdSelector = createSelector(cardDialogSelector, ({ lineId }) => {
  return lineId
})
/**
 * @type {function(PlottrState): (Null | number)}
 */
export const cardDialogBeatIdSelector = createSelector(cardDialogSelector, ({ beatId }) => {
  return beatId
})
/**
 * @type {function(PlottrState): boolean}
 */
export const isCardDialogVisibleSelector = createSelector(cardDialogSelector, ({ isOpen }) => {
  return isOpen
})

/**
 * @type {function(PlottrState): boolean}
 */
export const isBookDialogVisibleSelector = createSelector(bookDialogSelector, ({ isOpen }) => {
  return isOpen
})

/**
 * @type {function(PlottrState): ('series' | number | null)}
 */
export const bookDialogBookIdSelector = createSelector(bookDialogSelector, ({ bookId }) => {
  return bookId
})

/**
 * @type {function(PlottrState): (number)}
 */
export const bookNumberSelector = createSelector(
  allBookIdsSelector,
  bookDialogBookIdSelector,
  (allBookIds, bookDialogBookId) => {
    if (typeof bookDialogBookId === 'number') {
      return allBookIds.indexOf(bookDialogBookId) + 1
    }

    return allBookIds.length + 1
  }
)

/**
 * @type {function(PlottrState): number}
 */
export const outlineScrollPositionSelector = createSelector(
  uiSelector,
  ({ outlineScrollPosition }) => outlineScrollPosition
)

/**
 * @type {function(PlottrState): number}
 */
export const outlinePlanScrollPositionSelector = createSelector(
  uiSelector,
  ({ outlinePlanScrollPosition }) => outlinePlanScrollPosition
)

/**
 * @type {function(PlottrState): boolean}
 */
export const restructureModalOpenSelector = createSelector(
  uiSelector,
  ({ resturctureTimelineModal }) => {
    return !!resturctureTimelineModal?.open
  }
)

/**
 * @type {function(PlottrState): boolean}
 */
export const characterAttributesDialogOpenSelector = createSelector(
  characterTabSelector,
  ({ attributesDialogOpen }) => {
    return attributesDialogOpen
  }
)
/**
 * @type {function(PlottrState): boolean}
 */
export const characterCategoriesDialogOpenSelector = createSelector(
  characterTabSelector,
  ({ categoriesDialogOpen }) => {
    return categoriesDialogOpen
  }
)

/**
 * @type {function(PlottrState): boolean}
 */
export const characterCategoriesCollapsedSelector = createSelector(
  characterTabSelector,
  ({ collapsedCategories }) => {
    return collapsedCategories
  }
)

/**
 * @type {function(PlottrState): boolean}
 */
export const editingSelectedCharacterSelector = createSelector(
  characterTabSelector,
  ({ editingSelected }) => {
    return editingSelected
  }
)
/**
 * @type {function(PlottrState): boolean}
 */
export const characterTemplatePickerVisibleSelector = createSelector(
  characterTabSelector,
  ({ showTemplatePicker }) => {
    return showTemplatePicker
  }
)
/**
 * @type {function(PlottrState): boolean}
 */
export const creatingCharacterSelector = createSelector(characterTabSelector, ({ creating }) => {
  return creating
})
/**
 * @type {function(PlottrState): (Null | CharacterTemplate | CharacterCustomTemplate)}
 */
export const characterTemplateDataSelector = createSelector(
  characterTabSelector,
  ({ templateData }) => {
    return templateData
  }
)
/**
 * @type {function(PlottrState): boolean}
 */
export const characterFilterVisibleSelector = createSelector(
  characterTabSelector,
  ({ filterVisible }) => {
    return filterVisible
  }
)
/**
 * @type {function(PlottrState): boolean}
 */
export const characterSortVisibleSelector = createSelector(
  characterTabSelector,
  ({ sortVisible }) => {
    return sortVisible
  }
)
/**
 * @type {function(PlottrState): boolean}
 */
export const characterDetailsVisible = createSelector(
  characterTabSelector,
  ({ detailsVisible }) => {
    if (typeof detailsVisible === 'undefined') {
      return true
    }

    return detailsVisible
  }
)
/**
 * @type {function(PlottrState): CharacterTabCharacterEditor}
 */
export const characterEditorSelector = createSelector(
  characterTabSelector,
  ({ characterEditor }) => {
    return characterEditor || {}
  }
)
/**
 * @type {function(PlottrState): boolean}
 */
export const characterEditorIsDeletingSelector = createSelector(
  characterEditorSelector,
  ({ deleting }) => {
    return deleting
  }
)
/**
 * @type {function(PlottrState): boolean}
 */
export const characterEditorIsRemovingTemplateSelector = createSelector(
  characterEditorSelector,
  ({ removing }) => {
    return removing
  }
)
/**
 * @type {function(PlottrState): (String | Null)}
 */
export const characterEditorTemplateBeingRemovedSelector = createSelector(
  characterEditorSelector,
  ({ removeWhichTemplate }) => {
    return removeWhichTemplate
  }
)
/**
 * @type {function(PlottrState): number}
 */
export const characterEditorActiveTabSelector = createSelector(
  characterEditorSelector,
  ({ activeTab }) => {
    return activeTab ?? 1
  }
)
/**
 * @type {function(PlottrState): boolean}
 */
export const characterEditorShowTemplatePickerSelector = createSelector(
  characterEditorSelector,
  ({ showTemplatePicker }) => {
    return showTemplatePicker
  }
)
/**
 * @type {function(PlottrState): Array<Focus>}
 */
export const characterFociSelector = createSelector(characterTabSelector, ({ focus }) => {
  return focus ?? []
})
/**
 * @type {function(PlottrState): (number | 'all')}
 */
const characterAttributeTabSelector = createSelector(
  selectedCharacterAttributeTabSelector,
  showBookTabsSelector,
  (selectedTab, showTabs) => {
    return !showTabs ? 'all' : selectedTab
  }
)
/**
 * @type {function(PlottrState, number): Array<Focus>}
 */
export const characterCurrentFociSelector = createSelector(
  characterFociSelector,
  selectedCharacterSelector,
  characterAttributeTabSelector,
  (foci, characterId, characterTab) => {
    return foci.filter((focus) => {
      return (
        focus.path[1] === characterId &&
        (focus.path.length === 3 ||
          (focus.path.length === 4 && focus.path[3] === characterTab) ||
          (focus.path.length === 6 && focus.path[5] === characterTab) ||
          (focus.path.length === 5 && focus.path[4] === characterTab))
      )
    })
  }
)
/**
 * @type {function(PlottrState): boolean}
 */
export const relationshipEditorOpenSelector = createSelector(
  characterTabSelector,
  ({ relationshipEditorOpen }) => {
    return !!relationshipEditorOpen
  }
)

/**
 * @type {function(PlottrState): SearchDialogUi}
 */
export const searchDialogSelector = createSelector(uiSelector, ({ searchDialog }) => {
  return searchDialog
})
/**
 * @type {function(PlottrState): boolean}
 */
export const searchDialogIsOpenSelector = createSelector(searchDialogSelector, ({ isOpen }) => {
  return !!isOpen
})
/**
 * @type {function(PlottrState): boolean}
 */
export const isJumpingSelector = createSelector(searchDialogSelector, ({ jumping }) => {
  return jumping
})
/**
 * @type {function(PlottrState): String}
 */
export const searchDialogSearchTermSelector = createSelector(searchDialogSelector, ({ term }) => {
  return term
})
/**
 * @type {function(PlottrState): (number | Null)}
 */
export const searchDialogCurrentHitIndexSelector = createSelector(
  searchDialogSelector,
  ({ currentHitIndex }) => {
    return currentHitIndex
  }
)
/**
 * @type {function(PlottrState): boolean}
 */
export const searchDialogIsScanningSelector = createSelector(
  searchDialogSelector,
  ({ scanning }) => {
    return scanning
  }
)
/**
 * @type {function(PlottrState): boolean}
 */
export const searchDialogIsReplacingSelector = createSelector(
  searchDialogSelector,
  ({ replacing }) => {
    return !!replacing
  }
)
/**
 * @type {function(PlottrState): String}
 */
export const searchReplacementTextSelector = createSelector(
  searchDialogSelector,
  ({ replacement }) => {
    return replacement
  }
)
/**
 * @type {function(PlottrState): Array<SearchHit>}
 */
export const hitsMarkedForReplacementSelector = createSelector(
  searchDialogSelector,
  ({ hitsToReplace }) => {
    return hitsToReplace ?? []
  }
)
/**
 * @type {function(PlottrState): boolean}
 */
export const searchModalReplaceWordSelector = createSelector(
  searchDialogSelector,
  ({ replaceWord }) => {
    return replaceWord
  }
)

/**
 * @type {function(PlottrState): 'search' | 'replace' | 'bookmarks'}
 */
export const searchModalActiveTabSelector = createSelector(
  searchDialogSelector,
  ({ activeTab }) => {
    return activeTab ?? 'search'
  }
)

/**
 * @type {function(PlottrState): Array<SearchHit>}
 */
export const projectSearchHitsSelector = createSelector(
  searchDialogSearchTermSelector,
  seriesSelector,
  allBooksAsArraySelector,
  searchModalReplaceWordSelector,
  (term, series, books, replaceWord) => {
    if (term === '' || !term || term.length < 3) {
      return []
    }

    const bookLikeMatch = (entity, prefix, id) => {
      const nameKey = prefix === 'book' ? 'title' : 'name'
      const which = id ? `/${id}` : ''
      const nameMatch = entity[nameKey]?.matchAll(literalRegExp(term, replaceWord))
      const genreMatch = entity.genre?.matchAll(literalRegExp(term, replaceWord))
      const premiseMatch = entity.premise?.matchAll(literalRegExp(term, replaceWord))
      const themeMatch = entity.theme?.matchAll(literalRegExp(term, replaceWord))
      return [
        ...(nameMatch ? hits(`/project/${prefix}${which}/${nameKey}`, nameMatch) : []),
        ...(genreMatch ? hits(`/project/${prefix}${which}/genre`, genreMatch) : []),
        ...(premiseMatch ? hits(`/project/${prefix}${which}/premise`, premiseMatch) : []),
        ...(themeMatch ? hits(`/project/${prefix}${which}/theme`, themeMatch) : []),
      ]
    }
    const bookMatches = books.flatMap((book) => {
      return bookLikeMatch(book, 'book', book.id)
    })

    return [...bookLikeMatch(series, 'series'), ...bookMatches].filter((hit) => {
      return hit.hit.length > 0
    })
  }
)
/**
 * @type {function(PlottrState): Array<SearchHit>}
 */
export const timelineSearchHitsSelector = createSelector(
  searchDialogSearchTermSelector,
  allLinesSelector,
  allCardsSelector,
  cardsCustomAttributesSelector,
  searchModalReplaceWordSelector,
  (term, lines, cards, customAttributes, replaceWord) => {
    if (term === '' || !term || term.length < 3) {
      return []
    }

    const cardMatch = (card) => {
      const timeline = lines.find((line) => {
        return line.id == card.lineId
      })?.bookId
      const titleMatch = card.title.matchAll(literalRegExp(term, replaceWord))
      const descriptionText = serializeNoFormatting(card.description)
      const descriptionMatch = descriptionText.matchAll(literalRegExp(term, replaceWord))
      const cardCustomAttributes = customAttributes
        .filter((attribute) => {
          return typeof card[attribute.name] !== 'undefined'
        })
        .map((attribute) => {
          return {
            id: attribute.name,
            value: card[attribute.name],
          }
        })
      const customAttributeMatches = cardCustomAttributes.flatMap((attribute) => {
        const { id, value } = attribute
        const valueAsString =
          (Array.isArray(value) ? serializeNoFormatting(value) : String(value)) || ''
        const valueMatch = valueAsString.matchAll(literalRegExp(term, replaceWord))
        if (!valueMatch) {
          return []
        }
        return hits(
          `/timeline/${timeline}/card/${card.id}/customAttribute/${escapeUIPathElement(id)}`,
          valueMatch
        )
      })
      const templateMatches = card.templates.flatMap((template) => {
        const templateAttributeHit = (attribute) => {
          const name = attribute.name
          const value = attribute.value
          const valueAsString =
            (Array.isArray(value) ? serializeNoFormatting(value) : String(value)) || ''
          const valueMatch = valueAsString.matchAll(literalRegExp(term))
          if (!valueMatch) {
            return []
          }
          return hits(
            `/timeline/${timeline}/card/${card.id}/templateAttribute/${
              template.id
            }/${escapeUIPathElement(name)}`,
            valueMatch
          )
        }
        return template.attributes.flatMap(templateAttributeHit)
      })
      const titleMatches = hits(`/timeline/${timeline}/card/${card.id}/title`, titleMatch)
      const descriptionMatches = hits(
        `/timeline/${timeline}/card/${card.id}/description`,
        descriptionMatch
      )
      return [
        ...[titleMatches, descriptionMatches].flatMap((x) => x),
        ...customAttributeMatches,
        ...templateMatches,
      ]
    }

    return cards
      .flatMap((card) => {
        return cardMatch(card)
      })
      .filter((hit) => {
        return hit.hit.length > 0
      })
  }
)

const outlineViews = ['plan', 'fulltext', 'legacy']
/**
 * @type {function(PlottrState): Array<SearchHit>}
 */
export const outlineSearchHitsSelector = createSelector(
  searchDialogSearchTermSelector,
  allLinesSelector,
  allCardsSelector,
  searchModalReplaceWordSelector,
  showOutlineFulltextCardTitleSelector,
  (term, lines, cards, replaceWord, showCardTitle) => {
    if (term === '' || !term || term.length < 3) {
      return []
    }

    const cardMatch = (card, view) => {
      const titleMatch = card?.title?.matchAll(literalRegExp(term, replaceWord))
      const descriptionText = serializeNoFormatting(card.description)
      const descriptionMatch = descriptionText.matchAll(literalRegExp(term, replaceWord))
      const timeline = lines.find((line) => {
        return line.id == card.lineId
      })?.bookId
      return [
        titleMatch && ((view === 'fulltext' && showCardTitle) || view !== 'fulltext')
          ? hits(`/outline/${view}/${timeline}/card/${card.id}/title`, titleMatch)
          : [],
        // dont create hits for plan view tooltip description
        descriptionMatch && view !== 'plan'
          ? hits(`/outline/${view}/${timeline}/card/${card.id}/description`, descriptionMatch)
          : [],
      ].flatMap((x) => x)
    }

    return outlineViews.flatMap((view) =>
      cards
        .flatMap((c) => cardMatch(c, view))
        .filter((hit) => {
          return hit.hit.length > 0
        })
    )
  }
)
/**
 * @type {function(PlottrState): Array<SearchHit>}
 */
export const notesSearchHitsSelector = createSelector(
  searchDialogSearchTermSelector,
  allNotesSelector,
  noteCustomAttributesSelector,
  searchModalReplaceWordSelector,
  (term, notes, customAttributes, replaceWord) => {
    if (term === '' || !term || term.length < 3) {
      return []
    }

    const noteMatch = (note) => {
      const titleMatch = note.title.matchAll(literalRegExp(term, replaceWord))
      const contentText = serializeNoFormatting(note.content)
      const contentMatch = contentText.matchAll(literalRegExp(term, replaceWord))
      const noteCustomAttributes = customAttributes
        .filter((attribute) => {
          return typeof note[attribute.name] !== 'undefined'
        })
        .map((attribute) => {
          return [attribute.name, note[attribute.name]]
        })
      const customAttributeMatches = noteCustomAttributes.flatMap(([key, value]) => {
        const valueAsString =
          (Array.isArray(value) ? serializeNoFormatting(value) : String(value)) || ''
        const valueMatch = valueAsString.matchAll(literalRegExp(term, replaceWord))
        if (!valueMatch) {
          return []
        }
        return hits(`/notes/${note.id}/customAttribute/${escapeUIPathElement(key)}`, valueMatch)
      })
      return [
        ...[
          titleMatch ? hits(`/notes/${note.id}/title`, titleMatch) : [],
          contentMatch ? hits(`/notes/${note.id}/content`, contentMatch) : [],
        ].flatMap((x) => x),
        ...customAttributeMatches,
      ]
    }

    return notes.flatMap(noteMatch).filter((hit) => {
      return hit.hit.length > 0
    })
  }
)
/**
 * @type {function(PlottrState): Array<SearchHit>}
 */
export const charactersHitsSelector = createSelector(
  searchDialogSearchTermSelector,
  allCharactersSelector,
  attributesSelector,
  allBooksSelector,
  characterCustomAttributesSelector,
  searchModalReplaceWordSelector,
  characterSearchHits
)
/**
 * @type {function(PlottrState): Array<SearchHit>}
 */
export const placesHitsSelector = createSelector(
  searchDialogSearchTermSelector,
  allPlacesSelector,
  placeCustomAttributesSelector,
  searchModalReplaceWordSelector,
  (term, allPlaces, customAttributes, replaceWord) => {
    if (term === '' || !term || term.length < 3) {
      return []
    }

    const placeMatch = (place) => {
      const nameMatch = place.name.matchAll(literalRegExp(term, replaceWord))
      const descriptionMatch = place.description.matchAll(literalRegExp(term, replaceWord))
      const notesText = serializeNoFormatting(place.notes)
      const notesMatch = notesText.matchAll(literalRegExp(term, replaceWord))
      const placeCustomAttributes = customAttributes
        .filter((attribute) => {
          return typeof place[attribute.name] !== 'undefined'
        })
        .map((attribute) => {
          return [attribute.name, place[attribute.name]]
        })
      const customAttributeMatches = placeCustomAttributes.flatMap(([key, value]) => {
        const valueAsString =
          (Array.isArray(value) ? serializeNoFormatting(value) : String(value)) || ''
        const valueMatch = valueAsString.matchAll(literalRegExp(term, replaceWord))
        if (!valueMatch) {
          return []
        }
        return hits(`/places/${place.id}/customAttribute/${escapeUIPathElement(key)}`, valueMatch)
      })
      // TODO template attributes
      return [
        ...[
          nameMatch ? hits(`/places/${place.id}/name`, nameMatch) : [],
          descriptionMatch ? hits(`/places/${place.id}/description`, descriptionMatch) : [],
          notesMatch ? hits(`/places/${place.id}/notes`, notesMatch) : [],
        ].flatMap((x) => x),
        ...customAttributeMatches,
      ]
    }

    return allPlaces.flatMap(placeMatch).filter((hit) => {
      return hit.hit.length > 0
    })
  }
)
/**
 * @type {function(PlottrState): Array<SearchHit>}
 */
export const tagsSearchHitsSelector = createSelector(
  searchDialogSearchTermSelector,
  allTagsSelector,
  searchModalReplaceWordSelector,
  (term, allTags, replaceWord) => {
    if (term === '' || !term || term.length < 3) {
      return []
    }

    const tagMatch = (tag) => {
      const titleMatch = tag.title.matchAll(literalRegExp(term, replaceWord))
      return [titleMatch ? hits(`/tags/${tag.id}/title`, titleMatch) : []].flatMap((x) => x)
    }

    return allTags.flatMap(tagMatch).filter((hit) => {
      return hit.hit.length > 0
    })
  }
)
/**
 * @type {function(PlottrState): Array<SearchHit>}
 */
export const linesSearchHitsSelector = createSelector(
  searchDialogSearchTermSelector,
  allLinesSelector,
  searchModalReplaceWordSelector,
  (term, lines, replaceWord) => {
    if (term === '' || !term || term.length < 3) {
      return []
    } else {
      const lineHit = (line) => {
        const titleMatch = line.title.matchAll(literalRegExp(term, replaceWord))
        return [titleMatch ? hits(`/lines/${line.id}/title`, titleMatch) : []].flatMap((x) => x)
      }
      return lines.flatMap(lineHit).filter((hit) => {
        return hit.hit.length > 0
      })
    }
  }
)
/**
 * @type {function(PlottrState): Array<SearchHit>}
 */
export const beatHitsSelector = createSelector(
  searchDialogSearchTermSelector,
  allBeatsSelector,
  searchModalReplaceWordSelector,
  (term, allBeatTrees, replaceWord) => {
    if (term === '' || !term || term.length < 3) {
      return []
    } else {
      const beatHit = (bookId) => (beat) => {
        const titleMatch = beat.title.matchAll(literalRegExp(term, replaceWord))
        return [titleMatch ? hits(`/beats/${bookId}/${beat.id}/title`, titleMatch) : []].flatMap(
          (x) => x
        )
      }
      return Object.entries(allBeatTrees).reduce((acc, nextKeyValue) => {
        const [bookId, beatTree] = nextKeyValue
        const beats = Object.values(beatTree.index)
        return [
          ...acc,
          ...beats.flatMap(beatHit(bookId)).filter((hit) => {
            return hit.hit.length > 0
          }),
        ]
      }, [])
    }
  }
)

const createDeepEqualSelector = createSelectorCreator(defaultMemoize, isEqual)

/**
 * @typedef AllSearchHits
 * @property {Array<SearchHit>} project
 * @property {Array<SearchHit>} timeline
 * @property {Array<SearchHit>} outline
 * @property {Array<SearchHit>} notes
 * @property {Array<SearchHit>} characters
 * @property {Array<SearchHit>} places
 * @property {Array<SearchHit>} tags
 * @property {Array<SearchHit>} lines
 * @property {Array<SearchHit>} beats
 */

/**
 * @type {function(PlottrState): AllSearchHits}
 */
export const searchHitsSelector = createDeepEqualSelector(
  projectSearchHitsSelector,
  timelineSearchHitsSelector,
  outlineSearchHitsSelector,
  notesSearchHitsSelector,
  charactersHitsSelector,
  placesHitsSelector,
  tagsSearchHitsSelector,
  linesSearchHitsSelector,
  beatHitsSelector,
  (
    projectHits,
    timelineHits,
    outlineHits,
    notesHits,
    charactersHits,
    placesHits,
    tagsHits,
    lineHits,
    beatHits
  ) => {
    return {
      project: projectHits,
      timeline: timelineHits,
      outline: outlineHits,
      notes: notesHits,
      characters: charactersHits,
      places: placesHits,
      tags: tagsHits,
      lines: lineHits,
      beats: beatHits,
    }
  }
)

/**
 * @type {function(PlottrState): Array<SearchHit>}
 */
export const flatSearchHitsSelector = createDeepEqualSelector(
  projectSearchHitsSelector,
  timelineSearchHitsSelector,
  outlineSearchHitsSelector,
  notesSearchHitsSelector,
  charactersHitsSelector,
  placesHitsSelector,
  tagsSearchHitsSelector,
  linesSearchHitsSelector,
  beatHitsSelector,
  (
    projectHits,
    timelineHits,
    outlineHits,
    notesHits,
    charactersHits,
    placesHits,
    tagsHits,
    lineHits,
    beatHits
  ) => {
    return [
      ...projectHits,
      ...timelineHits,
      ...outlineHits,
      ...notesHits,
      ...charactersHits,
      ...placesHits,
      ...tagsHits,
      ...lineHits,
      ...beatHits,
    ]
  }
)

/**
 * @type {function(PlottrState): boolean}
 */
export const hasNoResultsSelector = createSelector(
  projectSearchHitsSelector,
  timelineSearchHitsSelector,
  outlineSearchHitsSelector,
  notesSearchHitsSelector,
  charactersHitsSelector,
  placesHitsSelector,
  tagsSearchHitsSelector,
  linesSearchHitsSelector,
  beatHitsSelector,
  (
    projectHits,
    timelineHits,
    outlineHits,
    notesHits,
    charactersHits,
    placesHits,
    tagsHits,
    lineHits,
    beatHits
  ) => {
    const total = [
      projectHits,
      timelineHits,
      outlineHits,
      notesHits,
      charactersHits,
      placesHits,
      tagsHits,
      lineHits,
      beatHits,
    ].reduce((acc, hits) => {
      return hits?.length + acc
    }, 0)

    return !total ? true : false
  }
)

/**
 * @type {function(PlottrState): OutlineTabUi}
 */
export const outlineSelector = createSelector(uiSelector, ({ outlineTab }) => {
  return outlineTab
})
/**
 * @type {function(PlottrState): (number | Null)}
 */
export const selectedOutlineCardSelector = createSelector(outlineSelector, ({ selectedCard }) => {
  return selectedCard
})
/**
 * @type {function(PlottrState): Array<Focus>}
 */
export const outlineFociSelector = createSelector(outlineSelector, ({ focus }) => {
  return focus ?? []
})
/**
 * @type {function(PlottrState): OutlineCardEditor}
 */
const outlineTabCardEditorSelector = createSelector(outlineSelector, ({ cardEditor }) => {
  return cardEditor
})
/**
 * @type {function(PlottrState): (number | Null)}
 */
export const editingOutlineCardSelector = createSelector(
  outlineTabCardEditorSelector,
  ({ editing }) => {
    return editing
  }
)
/**
 * @type {function(PlottrState): Array<Focus>}
 */
export const outlineCurrentFocusSelector = createSelector(
  outlineFociSelector,
  editingOutlineCardSelector,
  (foci, cardId) => {
    return foci.filter((focus) => {
      return focus.path?.[2] === cardId
    })
  }
)

/**
 * @type {function(PlottrState): NoteTabUi}
 */
export const noteTabSelector = createSelector(uiSelector, ({ noteTab }) => {
  return noteTab
})
/**
 * @type {function(PlottrState): (number | Null)}
 */
export const selectedNoteSelector = createSelector(noteTabSelector, ({ selectedNote }) => {
  return selectedNote
})
/**
 * @type {function(PlottrState): boolean}
 */
export const editingSelectedNoteSelector = createSelector(
  noteTabSelector,
  ({ editingSelected }) => {
    return editingSelected
  }
)
/**
 * @type {function(PlottrState): boolean}
 */
export const noteCategoriesDialogOpenSelector = createSelector(
  noteTabSelector,
  ({ categoriesDialogOpen }) => {
    return categoriesDialogOpen
  }
)
/**
 * @type {function(PlottrState): boolean}
 */
export const noteAttributesDialogOpenSelector = createSelector(
  noteTabSelector,
  ({ attributesDialogOpen }) => {
    return attributesDialogOpen
  }
)
/**
 * @type {function(PlottrState): boolean}
 */
export const noteFilterVisibleSelector = createSelector(noteTabSelector, ({ filterVisible }) => {
  return filterVisible
})
/**
 * @type {function(PlottrState): boolean}
 */
export const noteSortVisibleSelector = createSelector(noteTabSelector, ({ sortVisible }) => {
  return sortVisible
})
/**
 * @type {function(PlottrState): Array<Focus>}
 */
export const noteFociSelector = createSelector(noteTabSelector, ({ focus }) => {
  return focus ?? []
})
/**
 * @type {function(PlottrState): Array<Focus>}
 */
export const noteCurrentFocusSelector = createSelector(
  noteFociSelector,
  selectedNoteSelector,
  (foci, noteId) => {
    return foci.filter((focus) => {
      return focus.path?.[1] === noteId
    })
  }
)

/**
 * @type {function(PlottrState): PlaceTabUi}
 */
export const placeTabSelector = createSelector(uiSelector, ({ placeTab }) => {
  return placeTab
})
/**
 * @type {function(PlottrState): (number | Null)}
 */
export const selectedPlaceSelector = createSelector(placeTabSelector, ({ selectedPlace }) => {
  return selectedPlace
})
/**
 * @type {function(PlottrState): boolean}
 */
export const placeAttributeDialogIsOpenSelector = createSelector(
  placeTabSelector,
  ({ attributeDialogOpen }) => {
    return attributeDialogOpen
  }
)
/**
 * @type {function(PlottrState): boolean}
 */
export const editingSelectedPlaceSelector = createSelector(
  placeTabSelector,
  ({ editingSelected }) => {
    return editingSelected
  }
)
/**
 * @type {function(PlottrState): boolean}
 */
export const placesCategoriesOpenSelector = createSelector(
  placeTabSelector,
  ({ categoriesOpen }) => {
    return categoriesOpen
  }
)
/**
 * @type {function(PlottrState): boolean}
 */
export const placesFilterIsVisibleSelector = createSelector(
  placeTabSelector,
  ({ filterVisible }) => {
    return filterVisible
  }
)
/**
 * @type {function(PlottrState): boolean}
 */
export const placesSortIsVisibleSelector = createSelector(placeTabSelector, ({ sortVisible }) => {
  return sortVisible
})
/**
 * @type {function(PlottrState): Array<Focus>}
 */
export const placeFociSelector = createSelector(placeTabSelector, ({ focus }) => {
  return focus ?? []
})
/**
 * @type {function(PlottrState): Array<Focus>}
 */
export const placeCurrentFocusSelector = createSelector(
  placeFociSelector,
  selectedPlaceSelector,
  (foci, placeId) => {
    return foci.filter((focus) => {
      return focus.path?.[1] === placeId
    })
  }
)

/**
 * @type {function(PlottrState): TagTabUi}
 */
export const tagTabSelector = createSelector(uiSelector, ({ tagTab }) => {
  return tagTab
})

/**
 * @type {function(PlottrState): boolean}
 */
export const isTagTabFocusingSelector = createSelector(
  tagTabSelector,
  ({ focus, editingSelectedTab }) => {
    return focus?.length ? Boolean(focus?.length) : editingSelectedTab
  }
)

/**
 * @type {function(PlottrState): (number | Null)}
 */
export const selectedTagSelector = createSelector(tagTabSelector, ({ selectedTag }) => {
  return selectedTag
})
/**
 * @type {function(PlottrState): boolean}
 */
export const editingSelectedTagSelector = createSelector(
  tagTabSelector,
  ({ editingSelectedTab }) => {
    return editingSelectedTab
  }
)
/**
 * @param {PlottrState} _state
 * @param {number} id
 * @return {number}
 */
const tagIdSelector = (_state, id) => id
/**
 * @type {function(PlottrState, number): boolean}
 */
export const isEditingTagSelector = createSelector(
  selectedTagSelector,
  tagIdSelector,
  editingSelectedTagSelector,
  (selectedTagId, tagId, editing) => {
    return selectedTagId === tagId && editing
  }
)
/**
 * @type {function(PlottrState): Array<Focus>}
 */
export const allTagFociSelector = createSelector(tagTabSelector, ({ focus }) => {
  return focus ?? []
})
/**
 * @type {function(PlottrState): Array<Focus>}
 */
export const tagCurrentFociSelector = createSelector(
  selectedTagSelector,
  allTagFociSelector,
  (selectedTagId, foci) => {
    return foci.filter((focus) => {
      return focus.path.length && focus.path[1] === selectedTagId
    })
  }
)

/**
 * @type {function(PlottrState): ProjectTabUi}
 */
const projectTabSelector = createSelector(uiSelector, ({ projectTab }) => {
  return projectTab
})
/**
 * @type {function(PlottrState): Focus}
 */
export const projectCurrentFocusSelector = createSelector(
  projectTabSelector,
  isBookDialogVisibleSelector,
  bookDialogBookIdSelector,
  ({ focus }, bookDialogIsOpen, bookDialogId) => {
    const relevantFoci = (focus || []).filter((focus) => {
      return (
        (!bookDialogIsOpen && focus.path[0] !== 'book') ||
        (bookDialogIsOpen && focus.path[0] === 'book' && focus.path[1] === bookDialogId)
      )
    })
    return relevantFoci && relevantFoci[0]
  }
)
/**
 * @type {function(PlottrState): Array<Focus>}
 */
export const projectAllFociSelector = createSelector(projectTabSelector, ({ focus }) => {
  return focus ?? []
})

/**
 * @type {function(PlottrState): TimelineUi}
 */
const timelineSelector = createSelector(uiSelector, ({ timeline }) => {
  return timeline
})
/**
 * @type {function(PlottrState): Array<Focus>}
 */
export const timelineFociSelector = createSelector(timelineSelector, ({ focus }) => {
  return focus ?? []
})
/**
 * @type {function(PlottrState): (Focus | Null)}
 */
export const timelineFocusCandidateSelector = createAggressiveDeepEqualSelector(
  timelineFociSelector,
  (foci) => {
    return foci.length && typeof foci[0] !== 'undefined' ? foci[0] : null
  }
)
/**
 * @param {PlottrState} _state
 * @param {number} beatId
 * @return {number}
 */
const beatIdSelector = (_state, beatId) => {
  return beatId
}
/**
 * @type {function(PlottrState, number): (Selection | DOMSelection | Null)}
 */
export const timelineBeatSelectionSelector = createAggressiveDeepEqualSelector(
  timelineFocusCandidateSelector,
  beatIdSelector,
  (focusCandidate, beatId) => {
    return focusCandidate &&
      Array.isArray(focusCandidate.path) &&
      focusCandidate.path[0] === 'beat' &&
      focusCandidate.path[1] === beatId &&
      focusCandidate.path[2] === 'title'
      ? focusCandidate.selection
      : null
  }
)
/**
 * @type {function(PlottrState): Array<Focus>}
 */
export const timelineCurrentFocusSelector = createSelector(
  timelineFociSelector,
  cardDialogCardIdSelector,
  (foci, cardId) => {
    return foci.filter((focus) => {
      return focus.path[1] === cardId
    })
  }
)

/**
 * @type {function(PlottrState): boolean}
 */
export const timelineFilterVisibleSelector = createSelector(
  timelineSelector,
  ({ filterVisible }) => {
    return filterVisible
  }
)
/**
 * @param {PlottrState} _state
 * @param {number} beatId
 * @return {number}
 */
const selectBeatId = (_state, beatId) => {
  return beatId
}
/**
 * @type {function(PlottrState): (Null | number)}
 */
const beatIdOfHeadingBeingEditedSelector = createSelector(
  timelineSelector,
  ({ beatHeadingTitleBeingEdited }) => {
    return beatHeadingTitleBeingEdited
  }
)
/**
 * @type {function(PlottrState, number): boolean}
 */
export const editingGivenBeatsTitleSelector = createSelector(
  beatIdOfHeadingBeingEditedSelector,
  selectBeatId,
  (beatIdBeingEdited, suppliedBeatId) => {
    return typeof beatIdBeingEdited === 'number' && beatIdBeingEdited === suppliedBeatId
  }
)
/**
 * @param {PlottrState} _state
 * @param {number} lineId
 * @return {number}
 */
const selectLineId = (_state, lineId) => {
  return lineId
}
/**
 * @type {function(PlottrState): (number | Null)}
 */
const lineIdOfPlotlineTitleBeingEditedSelector = createSelector(
  timelineSelector,
  ({ plotlineTitleBeingEdited }) => {
    return plotlineTitleBeingEdited
  }
)
/**
 * @type {function(PlottrState, number): boolean}
 */
export const editingGivenLinesTitleSelector = createSelector(
  lineIdOfPlotlineTitleBeingEditedSelector,
  selectLineId,
  (lineIdBeingEdited, suppliedLineId) => {
    return typeof lineIdBeingEdited === 'number' && lineIdBeingEdited === suppliedLineId
  }
)

/**
 * @type {function(PlottrState): DashboardModalUi}
 */
const dashboardModalSelector = createSelector(uiSelector, ({ dashboardModal }) => {
  return dashboardModal
})
/**
 * @type {function(PlottrState): DashboardModalView}
 */
export const dashboardModalViewSelector = createSelector(dashboardModalSelector, ({ view }) => {
  return view ?? null
})

/**
 * @type {function(PlottrState): boolean}
 */
export const arePreviewPanelsVisibleSelector = createSelector(
  uiSelector,
  dashboardModalViewSelector,
  isIdeaBoardOpenSelector,
  shouldShowPanelsWithIdeaBoardSelector,
  ({ showPreviewPanels }, dashboardModalView, isIdeaBoardOpen, shouldShowPanelsWithIdeaBoard) => {
    return (
      !dashboardModalView &&
      (!isIdeaBoardOpen || shouldShowPanelsWithIdeaBoard) &&
      (showPreviewPanels ?? true)
    )
  }
)

/**
 * @type {function(PlottrState): ImportModalUi}
 */
export const importPltrModalSelector = createSelector(uiSelector, ({ importModal }) => {
  return importModal
})

/**
 * @type {function(PlottrState): ImportModalUiData | Null}
 */
export const importPltrDataSelector = createSelector(importPltrModalSelector, (importModal) => {
  return importModal.data
})

/**
 * @type {function(PlottrState): boolean}
 */
export const shouldShowProAccountRecentFilesSelector = createSelector(
  importPltrModalSelector,
  (importModal) => {
    return Boolean(importModal?.showProRecentFiles) || false
  }
)

/**
 * @type {function(PlottrState): (ImportModalUiBookData | Null)}
 */
export const importModalBookDataSelector = createSelector(
  importPltrModalSelector,
  (importModal) => {
    return importModal.bookData ?? null
  }
)

/**
 * @type {function(PlottrState): boolean}
 */
export const isImportModalOpenSelector = createSelector(
  importPltrModalSelector,
  (importModal) => Boolean(importModal?.open) || false
)

/**
 * @type {function(PlottrState): ConflictResolutionModalUi}
 */
const conflictResolutionModalSelector = createSelector(
  uiSelector,
  ({ conflictResolutionModal }) => {
    return conflictResolutionModal
  }
)

/**
 * @type {function(PlottrState): boolean}
 */
export const conflictResolutionModalIsOpenSelector = createSelector(
  conflictResolutionModalSelector,
  ({ open }) => {
    return !!open
  }
)

/**
 * Selector that retrieves metadata about a card being hovered in the outline fulltext view
 * @param {Object} state - The Redux state object
 * @return {Object|null} An object containing metadata about the hovered card, or null if no card is found:
 *   - cardId {number} The ID of the hovered card
 *   - title {string} The title of the hovered card
 *   - relatedCharacters {string[]} Array of character names associated with the card
 *   - relatedPlaces {string[]} Array of place names associated with the card
 *   - relatedTags {string[]} Array of tag titles associated with the card
 *   - relatedBook {string|null} Title of the book containing this card, if any
 *   - templates {string[]} Array of template names
 */
/**
 * @type {function(PlottrState): ({ relatedCharacters: Array<String>, relatedPlaces: Array<String>, relatedTags: Array<Tag> } | Null)}
 */
export const outlineFulltextHoverDetailsMetadataSelector = createSelector(
  allCardsSelector,
  outlineFulltextHoverDetailsSelector,
  charactersSortedAtoZSelector,
  placesSortedAtoZSelector,
  sortedTagsSelector,
  (cards, hoverDetails, characters, places, tags) => {
    const cardId = hoverDetails?.cardId
    if (!cardId) return null

    // Retrieve the card
    const retrievedCard = cards.find((card) => card.id === cardId)
    if (!retrievedCard) return null

    // Create lookup maps for faster access
    const characterMap = Object.fromEntries(characters.map((char) => [char.id, char.name]))
    const placeMap = Object.fromEntries(places.map((place) => [place.id, place.name]))

    // Retrieve related characters, places, and tags
    const relatedCharacters =
      retrievedCard.characters?.map((id) => characterMap[id]).filter(Boolean) || []
    const relatedPlaces = retrievedCard.places?.map((id) => placeMap[id]).filter(Boolean) || []
    const relatedTags =
      retrievedCard.tags
        ?.map((id) => tags.find((tag) => tag.id === id))
        .reduce((acc, next) => {
          if (next) {
            return [...acc, next]
          } else {
            return acc
          }
        }, []) || []

    return {
      relatedCharacters,
      relatedPlaces,
      relatedTags,
    }
  }
)

/**
 * @type {function(PlottrState): ('plotline'|'card')}
 */
export const smallTimelineCardColorSourceSelector = createSelector(
  timelineSelector,
  ({ smallTimelineCardColorSource }) => {
    return smallTimelineCardColorSource || 'plotline'
  }
)

/**
 * @type {function(PlottrState): DocumentationUi}
 */
export const documentationSelector = createSelector(uiSelector, ({ documentation }) => {
  return documentation ?? uiState.documentation
})

/**
 * @type {function(PlottrState): DocumentationType}
 */
export const documentationTypeSelector = createSelector(documentationSelector, (documentation) => {
  return documentation?.documentationType ?? 'advanced-export'
})

/**
 * @type {function(PlottrState): boolean}
 */
export const documentationModalIsOpenSelector = createSelector(
  documentationSelector,
  (documentation) => {
    return !!documentation?.open
  }
)

/**
 * @type {function(PlottrState): Array<IdeaItem>}
 */
export const visibleSearchedIdeasSelector = createSelector(
  allIdeasSelector,
  ideasSearchTermSelector,
  (allIdeaBoard, searchTerm) => {
    if (!searchTerm) {
      return allIdeaBoard
    }
    const lowSearchTerms = searchTerm
      .toLowerCase()
      .split(' ')
      .filter((x) => x)

    const filteredIdeas = allIdeaBoard.filter((idea) => {
      return outOfOrderSearch(lowSearchTerms, JSON.stringify(idea).toLowerCase())
    })

    return filteredIdeas
  }
)

/**
 * When used as a comparator, sorts ideas by most recent first.
 *
 * @param {PocketIdea} a
 * @param {PocketIdea} b
 * @return {number}
 */
const compareIdeasByDate = (a, b) =>
  new Date(b.createdISOTimestamp).getTime() - new Date(a.createdISOTimestamp).getTime()

/**
 * @type {function(PlottrState): Ideas}
 */
export const ideasFromPocketSorrtedByCreatedDateSelector = createSelector(
  ideasFromPocketSelector,
  (allPocketIdeas) => {
    const shallowCopied = [...allPocketIdeas]
    shallowCopied.sort(compareIdeasByDate)
    return shallowCopied
  }
)

/**
 * @type {function(PlottrState): Ideas}
 */
export const visibleSearchedIdeasFromPocketSelector = createSelector(
  ideasFromPocketSorrtedByCreatedDateSelector,
  ideasFromPocketThatWereUsedSelector,
  ideasSearchTermSelector,
  (allPocketIdeas, ideasUsed, searchTerm) => {
    /**
     * @param {Idea} _idea
     * @return {boolean}
     */
    const isNotUsed = ({ id, deviceId }) => {
      return !ideasUsed.some((usedIdea) => {
        return usedIdea.ideaId === id && usedIdea.deviceId === deviceId
      })
    }
    if (!searchTerm) {
      return allPocketIdeas.filter(isNotUsed)
    } else {
      const lowercaseSearchTerms = searchTerm
        .toLowerCase()
        .split(' ')
        .filter((x) => x)
      return allPocketIdeas.filter((idea) => {
        return (
          isNotUsed(idea) &&
          outOfOrderSearch(lowercaseSearchTerms, JSON.stringify(idea).toLowerCase())
        )
      })
    }
  }
)

/**
 * @param {Ideas} ideas
 * @param {Threads} threads
 * @return {Record<String, Ideas>}
 */
const groupIdeasByThreadName = (ideas, threads) => {
  const uniqueThreadNames = uniq(
    threads.map(({ name }) => {
      return name
    })
  )
  /**
   * @type {Record<String, Ideas>}
   */
  const ideasByThreadName = uniqueThreadNames.reduce((acc, name) => {
    return {
      ...acc,
      [name]: ideas.filter((idea) => {
        const ideasThread = threads.find(({ id, deviceId }) => {
          return idea.threadId?.threadId === id && idea.threadId?.deviceId === deviceId
        })
        return ideasThread?.name === name
      }),
    }
  }, {})
  return Object.entries(ideasByThreadName).reduce((acc, next) => {
    const [threadName, ideasInThread] = next
    if (ideasInThread.length > 0) {
      return {
        ...acc,
        [threadName]: ideasInThread,
      }
    } else {
      return acc
    }
  }, {})
}

/**
 * @type {function(PlottrState): Record<String, Ideas>}
 */
export const visibleSearchedIdeasFromPocketGroupedByThreadNameSelector = createSelector(
  visibleSearchedIdeasFromPocketSelector,
  threadsFromPocketSelector,
  groupIdeasByThreadName
)

/**
 * @type {function(PlottrState): Ideas}
 */
export const visibleSearchedIdeasUsedFromPocketSelector = createSelector(
  ideasFromPocketSorrtedByCreatedDateSelector,
  ideasFromPocketThatWereUsedSelector,
  ideasSearchTermSelector,
  (allPocketIdeas, ideasUsed, searchTerm) => {
    /**
     * @param {Idea} _idea
     * @return {boolean}
     */
    const isUsed = ({ id, deviceId }) => {
      return ideasUsed.some((usedIdea) => {
        return usedIdea.ideaId === id && usedIdea.deviceId === deviceId
      })
    }
    if (!searchTerm) {
      return allPocketIdeas.filter(isUsed)
    } else {
      const lowercaseSearchTerms = searchTerm
        .toLowerCase()
        .split(' ')
        .filter((x) => x)
      return allPocketIdeas.filter((idea) => {
        return (
          isUsed(idea) && outOfOrderSearch(lowercaseSearchTerms, JSON.stringify(idea).toLowerCase())
        )
      })
    }
  }
)

/**
 * @type {function(PlottrState): Record<String, Ideas>}
 */
export const visibleSearchedIdeasUsedFromPocketGroupedByThreadNameSelector = createSelector(
  visibleSearchedIdeasUsedFromPocketSelector,
  threadsFromPocketSelector,
  groupIdeasByThreadName
)

/**
 * @type {function(PlottrState): Record<String, String>}
 */
export const stringifiedBookmarksByIdSelector = createSelector(
  allBookmarkedItemsSelector,
  (bookmarks) => {
    return bookmarks.reduce((acc, nextItem) => {
      return {
        ...acc,
        [`${nextItem.kind}-${nextItem.id}`]: JSON.stringify(nextItem).toLowerCase(),
      }
    }, {})
  }
)

/**
 * @type {function(PlottrState): Record<BookmarkEntityKind, Array<BookmarkItem>> | {}}
 */
export const visibleSearchedBookmarksSelector = createSelector(
  bookmarksByKindSelector,
  stringifiedBookmarksByIdSelector,
  bookmarksSearchTermSelector,
  (bookmarksByKind, stringifiedBookmarksById, searchTerm) => {
    if (!searchTerm) return bookmarksByKind
    const lowSearchTerms = searchTerm
      .toLowerCase()
      .split(' ')
      .filter((x) => x)

    return Object.entries(bookmarksByKind).reduce((acc, nextKind) => {
      const [key, bookmarks] = nextKind
      const newBoomarks = bookmarks.filter(({ id, kind }) => {
        return outOfOrderSearch(lowSearchTerms, stringifiedBookmarksById[`${kind}-${id}`])
      })
      if (newBoomarks.length > 0) {
        return {
          ...acc,
          [key]: newBoomarks,
        }
      } else {
        return acc
      }
    }, {})
  }
)

/**
 * @type {function(PlottrState): boolean}
 */
export const hasNoBookmarksSearchResultsSelector = createSelector(
  visibleSearchedBookmarksSelector,
  (bookmarksByKind) => {
    const totalResults = Object.values(bookmarksByKind).reduce((acc, nextKind) => {
      return Number(nextKind?.length) + acc
    }, 0)
    return !totalResults
  }
)

/**
 * @param {PlottrState} _state
 * @param {EntityKind} kind
 * @return {EntityKind}
 */
const panelKindSelector = (_state, kind) => {
  return kind
}
/**
 * @param {PlottrState} _state
 * @param {EntityKind} _kind
 * @param {number} id
 * @return {number}
 */
const entityIdSelector = (_state, _kind, id) => {
  return id
}

/**
 * @type {function(PlottrState, PreviewPanelEntityKind, number): boolean}
 */
export const isPinnedSelector = createSelector(
  allPreviewPanelsAsFlatArraySelector,
  panelKindSelector,
  entityIdSelector,
  (panels, kind, id) => {
    return panels.some((item) => {
      return item.id === id && item.kind === kind
    })
  }
)

/**
 * @param {PlottrState} _state
 * @param {EntityKind} kind
 * @return {EntityKind}
 */
const bookmarksKindSelector = (_state, kind) => {
  return kind
}

/**
 * @type {function(PlottrState, BookmarkEntityKind, number): boolean}
 */
export const isBookmarkedSelector = createSelector(
  allBookmarkedItemsSelector,
  bookmarksKindSelector,
  entityIdSelector,
  (bookmarks, kind, id) => {
    return bookmarks.some((item) => {
      return item.id === id && item.kind === kind
    })
  }
)

/**
 * @type {function(PlottrState): boolean}
 */
export const isNotesTabListViewSelector = createSelector(
  noteTabSelector,
  ({ showAsList }) => showAsList ?? true
)

/**
 * @type {function(PlottrState): boolean}
 */
export const isPlacesTabListViewSelector = createSelector(
  placeTabSelector,
  ({ showAsList }) => showAsList ?? true
)

/**
 * @type {function(PlottrState): boolean}
 */
export const placeCategoriesCollapsedSelector = createSelector(
  placeTabSelector,
  ({ collapsedCategories }) => collapsedCategories ?? false
)

/**
 * @type {function(PlottrState): boolean}
 */
export const noteCategoriesCollapsedSelector = createSelector(
  noteTabSelector,
  ({ collapsedCategories }) => collapsedCategories ?? false
)

/**
 * @type {function(PlottrState): MobileControlsModal}
 */
export const mobileControlsModalSelector = createSelector(
  uiSelector,
  ({ mobileControlsModal }) => mobileControlsModal ?? {}
)

/**
 * @type {function(PlottrState): boolean}
 */
export const isMobileControlsModalOpenSelector = createSelector(
  mobileControlsModalSelector,
  ({ open }) => open ?? false
)
